/**
 * /tracking/impressions — impression tracking workspace (feature #168).
 *
 * Declares the TanStack Router route and delegates rendering to
 * `ImpressionTrackingPage`. The flat-file naming convention uses dots as path
 * separators, so `tracking.impressions.tsx` maps to `/tracking/impressions`.
 *
 * Search params:
 *   ?advertiser_id=adv_xxx   Pre-scope the template form + list filter.
 *   ?campaign_id=cmp_xxx     Pre-scope the template form + list filter.
 */

import { createRoute } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { ImpressionTrackingPage } from "@/features/impression-tracking/ImpressionTrackingPage";

// ---------------------------------------------------------------------------
// Search validation
// ---------------------------------------------------------------------------

interface TrackingImpressionsSearch {
	advertiser_id?: string;
	campaign_id?: string;
}

const SCOPE_ID_MAX = 120;

function coerceScopeId(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined;
	const trimmed = raw.trim();
	if (!trimmed || trimmed.length > SCOPE_ID_MAX) return undefined;
	return trimmed;
}

// ---------------------------------------------------------------------------
// Route — mounts at `/tracking/impressions`.
// ---------------------------------------------------------------------------

export const trackingImpressionsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/impressions",
	validateSearch: (raw: Record<string, unknown>): TrackingImpressionsSearch => {
		const out: TrackingImpressionsSearch = {};
		const adv = coerceScopeId(raw.advertiser_id);
		if (adv) out.advertiser_id = adv;
		const camp = coerceScopeId(raw.campaign_id);
		if (camp) out.campaign_id = camp;
		return out;
	},
	component: TrackingImpressionsRoutePage,
});

export default trackingImpressionsRoute;

// Wrapper reads the search params so the page can deep-link without prop
// drilling from the router call site.
export function TrackingImpressionsRoutePage() {
	const { advertiser_id, campaign_id } = trackingImpressionsRoute.useSearch();
	return (
		<ImpressionTrackingPage
			initialAdvertiserId={advertiser_id}
			initialCampaignId={campaign_id}
		/>
	);
}

// Re-export for the wire-up cycle / root registry import.
export { ImpressionTrackingPage } from "@/features/impression-tracking/ImpressionTrackingPage";
